'use client';

/**
 * AuditActivityTimeline Component
 *
 * Displays a timeline of audit activity events with user avatars,
 * action type filtering, date grouping, and color-coded severity.
 *
 * @see Issue #234 - Entity Audit Activity Logs
 */

import AddIcon from '@mui/icons-material/Add';
import AssignIcon from '@mui/icons-material/Assignment';
import ApproveIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/CloudDownload';
import UploadIcon from '@mui/icons-material/CloudUpload';
import CommentIcon from '@mui/icons-material/Comment';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ErrorIcon from '@mui/icons-material/Error';
import FilterIcon from '@mui/icons-material/FilterList';
import InfoIcon from '@mui/icons-material/Info';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import StatusChangeIcon from '@mui/icons-material/SwapHoriz';
import ViewIcon from '@mui/icons-material/Visibility';
import WarningIcon from '@mui/icons-material/Warning';
import { Avatar, Box, Chip, CircularProgress, Collapse, IconButton, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useMemo, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import type { NeonAccent } from '../../theme/palette';
import { getNeonStyles, severityColors } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';
import { iconSizes } from '../../theme/typography';
import type { AuditActionType, AuditActivityTimelineProps, AuditSeverity, AuditTimelineEvent } from '../../types/audit';
import { formatCompactRelativeTime, formatFullDateTime, safeToIsoString } from '../../utils/formatters';
import { GlassCard } from '../GlassCard/GlassCard';
import { GlassTooltip } from '../GlassTooltip';
import { LoadingOverlay } from '../LoadingOverlay';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get icon for action type
 */
function getActionIcon(action: AuditActionType | string) {
  switch (action) {
    case 'create':
      return <AddIcon fontSize="small" />;
    case 'update':
      return <EditIcon fontSize="small" />;
    case 'delete':
      return <DeleteIcon fontSize="small" />;
    case 'view':
    case 'read':
      return <ViewIcon fontSize="small" />;
    case 'login':
      return <LoginIcon fontSize="small" />;
    case 'logout':
      return <LogoutIcon fontSize="small" />;
    case 'approve':
      return <ApproveIcon fontSize="small" />;
    case 'reject':
      return <ErrorIcon fontSize="small" />;
    case 'assign':
      return <AssignIcon fontSize="small" />;
    case 'status_change':
      return <StatusChangeIcon fontSize="small" />;
    case 'comment':
      return <CommentIcon fontSize="small" />;
    case 'upload':
      return <UploadIcon fontSize="small" />;
    case 'download':
      return <DownloadIcon fontSize="small" />;
    case 'send':
      return <SendIcon fontSize="small" />;
    default:
      return <InfoIcon fontSize="small" />;
  }
}

/**
 * Get color for action type
 */
function getActionColor(action: AuditActionType | string): 'success' | 'error' | 'warning' | 'info' | 'default' {
  switch (action) {
    case 'create':
    case 'approve':
      return 'success';
    case 'delete':
    case 'reject':
      return 'error';
    case 'update':
    case 'status_change':
    case 'assign':
      return 'info';
    case 'login':
    case 'logout':
    case 'upload':
    case 'download':
      return 'warning';
    default:
      return 'default';
  }
}

/**
 * Get color for severity
 */
function getSeverityColor(severity?: AuditSeverity): string {
  switch (severity) {
    case 'critical':
      return severityColors.critical;
    case 'error':
      return severityColors.error;
    case 'warning':
      return severityColors.warning;
    case 'info':
    default:
      return severityColors.info;
  }
}

/**
 * Get severity icon
 */
function getSeverityIcon(severity?: AuditSeverity) {
  switch (severity) {
    case 'critical':
    case 'error':
      return <ErrorIcon fontSize="inherit" />;
    case 'warning':
      return <WarningIcon fontSize="inherit" />;
    case 'info':
    default:
      return <InfoIcon fontSize="inherit" />;
  }
}

/**
 * Group events by day
 */
function groupEventsByDay(events: AuditTimelineEvent[]): Map<string, AuditTimelineEvent[]> {
  const groups = new Map<string, AuditTimelineEvent[]>();

  for (const event of events) {
    // Skip events with malformed timestamps rather than crashing the
    // entire timeline render — safeToIsoString returns '' on invalid input.
    const iso = safeToIsoString(event.createdAt);
    if (!iso) continue;
    const dateKey = iso.split('T')[0]!;
    if (!groups.has(dateKey)) {
      groups.set(dateKey, []);
    }
    groups.get(dateKey)!.push(event);
  }

  return groups;
}

// ============================================================================
// Styled Components
// ============================================================================

const StyledContainer = styled(Box)({
  width: '100%',
});

const StyledHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
}));

const StyledFilterChips = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

interface StyledTimelineItemProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}

const StyledTimelineItem = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledTimelineItemProps>(({ theme, $isDark, $neonAccent }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    display: 'flex',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    borderRadius: 12,
    background: glass.background,
    border: `1px solid ${neonStyles ? neonStyles.border : glass.border}`,
    transition: transitionPresets.button,
    cursor: 'pointer',

    '&:hover': {
      background: glass.backgroundHover,
      border: `1px solid ${neonStyles ? neonStyles.border : glass.borderHover}`,
    },
  };
});

const StyledDayDivider = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  margin: theme.spacing(2, 0),

  '&::before, &::after': {
    content: '""',
    flex: 1,
    height: 1,
    background: theme.palette.divider,
  },
}));

const StyledEmptyState = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ theme, $isDark }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(5),
    borderRadius: 16,
    background: glass.background,
    border: `1px solid ${glass.border}`,
    textAlign: 'center',
  };
});

const StyledLoadMoreButton = styled(motion.button, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean; $neonAccent?: NeonAccent }>(({ theme, $isDark, $neonAccent }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    padding: '12px 20px',
    marginTop: 16,
    borderRadius: 12,
    border: `1px solid ${neonStyles ? neonStyles.border : glass.border}`,
    background: neonStyles ? neonStyles.background : glass.background,
    color: neonStyles ? neonStyles.base : theme.palette.text.secondary,
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: transitionPresets.button,
    fontFamily: theme.typography.fontFamily,

    '&:hover': {
      background: glass.backgroundHover,
      border: `1px solid ${glass.borderHover}`,
    },

    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  };
});

// ============================================================================
// Component
// ============================================================================

export function AuditActivityTimeline({
  events,
  loading = false,
  error,
  maxItems,
  groupBy = 'day',
  filter,
  onFilterChange,
  onLoadMore,
  hasMore = false,
  onEventClick,
  showAvatars = true,
  relativeTime = true,
  variant = 'glass',
  neonAccent,
  emptyMessage,
  title,
  showCard = true,
}: AuditActivityTimelineProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useI18n();
  const [showFilters, setShowFilters] = useState(false);

  // Available action types for filtering
  const actionTypes: AuditActionType[] = [
    'create',
    'update',
    'delete',
    'view',
    'login',
    'logout',
    'approve',
    'reject',
    'assign',
    'status_change',
    'comment',
    'upload',
    'download',
    'send',
  ];

  // Filter and limit events
  const filteredEvents = useMemo(() => {
    let result = [...events];

    // Apply action type filter
    if (filter?.actionTypes?.length) {
      result = result.filter((e) => filter.actionTypes!.includes(e.action as AuditActionType));
    }

    // Apply severity filter
    if (filter?.severities?.length) {
      result = result.filter((e) => filter.severities!.includes(e.severity!));
    }

    // Apply search filter
    if (filter?.searchQuery) {
      const query = filter.searchQuery.toLowerCase();
      result = result.filter(
        (e) =>
          e.description.toLowerCase().includes(query) ||
          e.actorName?.toLowerCase().includes(query) ||
          e.action.toLowerCase().includes(query),
      );
    }

    // Sort by date descending
    result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    // Apply max items limit
    if (maxItems && maxItems > 0) {
      result = result.slice(0, maxItems);
    }

    return result;
  }, [events, filter, maxItems]);

  // Group events by day if needed
  const groupedEvents = useMemo(() => {
    if (groupBy === 'day') {
      return groupEventsByDay(filteredEvents);
    }
    return new Map([['all', filteredEvents]]);
  }, [filteredEvents, groupBy]);

  // Handle filter toggle
  const handleFilterToggle = useCallback(
    (actionType: AuditActionType) => {
      if (!onFilterChange) return;

      const currentFilters = filter?.actionTypes || [];
      const newFilters = currentFilters.includes(actionType)
        ? currentFilters.filter((t) => t !== actionType)
        : [...currentFilters, actionType];

      onFilterChange({
        ...filter,
        actionTypes: newFilters.length > 0 ? newFilters : undefined,
      });
    },
    [filter, onFilterChange],
  );

  // Format date for day divider
  const formatDayDivider = (dateKey: string): string => {
    const date = new Date(dateKey);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (dateKey === today.toISOString().split('T')[0]) {
      return t('common.today');
    }
    if (dateKey === yesterday.toISOString().split('T')[0]) {
      return t('common.yesterday');
    }

    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
  };

  // Render timeline item
  const renderTimelineItem = (event: AuditTimelineEvent) => (
    <StyledTimelineItem
      key={event.id}
      $isDark={isDark}
      $neonAccent={neonAccent}
      onClick={() => onEventClick?.(event)}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      whileHover={{ scale: 1.01 }}
    >
      {/* Avatar or Icon */}
      {showAvatars && event.actorName ? (
        <Avatar
          src={event.actorAvatar}
          sx={{
            width: 36,
            height: 36,
            fontSize: '0.875rem',
            bgcolor: 'action.selected',
          }}
        >
          {event.actorName[0]?.toUpperCase()}
        </Avatar>
      ) : (
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${getActionColor(event.action)}.main`,
            color: 'white',
            opacity: 0.9,
          }}
        >
          {getActionIcon(event.action)}
        </Box>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontWeight: 600,
            }}
          >
            {event.actorName || t('common.system')}
          </Typography>
          <Chip
            label={event.action}
            size="small"
            color={getActionColor(event.action)}
            variant="outlined"
            sx={{ height: 20, fontSize: '0.75rem' }}
          />
          {event.severity && event.severity !== 'info' && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: getSeverityColor(event.severity),
                fontSize: '0.875rem',
              }}
            >
              {getSeverityIcon(event.severity)}
            </Box>
          )}
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 0.5,
          }}
        >
          {event.description}
        </Typography>
        <GlassTooltip title={formatFullDateTime(event.createdAt)}>
          <span>
            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
              }}
            >
              {relativeTime ? formatCompactRelativeTime(event.createdAt) : formatFullDateTime(event.createdAt)}
            </Typography>
          </span>
        </GlassTooltip>
      </Box>
    </StyledTimelineItem>
  );

  // Render content
  const renderContent = () => {
    // Error state
    if (error) {
      return (
        <StyledEmptyState $isDark={isDark}>
          <ErrorIcon sx={{ fontSize: iconSizes['3xl'], color: 'error.main', mb: 2 }} />
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        </StyledEmptyState>
      );
    }

    // Loading state
    if (loading && filteredEvents.length === 0) {
      return (
        <Box sx={{ position: 'relative', minHeight: 160 }}>
          <LoadingOverlay visible size={32} neonAccent={neonAccent} />
        </Box>
      );
    }

    // Empty state
    if (filteredEvents.length === 0) {
      return (
        <StyledEmptyState $isDark={isDark}>
          <InfoIcon sx={{ fontSize: iconSizes['3xl'], color: 'text.disabled', mb: 2 }} />
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
            }}
          >
            {emptyMessage || t('audit.timeline.empty')}
          </Typography>
        </StyledEmptyState>
      );
    }

    // Timeline with groups
    return (
      <>
        <AnimatePresence mode="popLayout">
          {Array.from(groupedEvents.entries()).map(([dateKey, dayEvents]) => (
            <Box key={dateKey}>
              {groupBy === 'day' && dateKey !== 'all' && (
                <StyledDayDivider>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    {formatDayDivider(dateKey)}
                  </Typography>
                </StyledDayDivider>
              )}
              {dayEvents.map(renderTimelineItem)}
            </Box>
          ))}
        </AnimatePresence>
        {/* Load More */}
        {hasMore && onLoadMore && (
          <StyledLoadMoreButton
            $isDark={isDark}
            $neonAccent={neonAccent}
            onClick={onLoadMore}
            disabled={loading}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            {loading ? (
              <>
                <CircularProgress size={16} sx={{ color: 'inherit' }} />
                {t('common.loading')}
              </>
            ) : (
              t('audit.timeline.loadMore')
            )}
          </StyledLoadMoreButton>
        )}
      </>
    );
  };

  // Main component
  const timeline = (
    <StyledContainer>
      {/* Header */}
      {(title || onFilterChange) && (
        <StyledHeader>
          {title && (
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
              }}
            >
              {title}
            </Typography>
          )}
          {onFilterChange && (
            <GlassTooltip title={t('audit.filter.action')}>
              <IconButton size="small" onClick={() => setShowFilters(!showFilters)}>
                <FilterIcon fontSize="small" />
              </IconButton>
            </GlassTooltip>
          )}
        </StyledHeader>
      )}

      {/* Filter Chips */}
      {onFilterChange && (
        <Collapse in={showFilters}>
          <StyledFilterChips>
            {actionTypes.map((actionType) => (
              <Chip
                key={actionType}
                label={actionType}
                size="small"
                variant={filter?.actionTypes?.includes(actionType) ? 'filled' : 'outlined'}
                color={getActionColor(actionType)}
                onClick={() => handleFilterToggle(actionType)}
                icon={getActionIcon(actionType)}
              />
            ))}
            {filter?.actionTypes?.length ? (
              <Chip
                label={t('audit.filter.clear')}
                size="small"
                variant="outlined"
                onClick={() => onFilterChange({ ...filter, actionTypes: undefined })}
                icon={<RefreshIcon fontSize="small" />}
              />
            ) : null}
          </StyledFilterChips>
        </Collapse>
      )}

      {/* Content */}
      {renderContent()}
    </StyledContainer>
  );

  // Wrap in card if needed
  if (showCard && variant === 'glass') {
    return <GlassCard neonAccent={neonAccent}>{timeline}</GlassCard>;
  }

  return timeline;
}

AuditActivityTimeline.displayName = 'AuditActivityTimeline';

export default AuditActivityTimeline;
